import { captureException } from "@/lib/sentry";
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiFetch";
import { siteConfigContract } from "@shared/contracts";

export interface SiteConfig {
  heroOpacity: number;
  heroCardBgOpacity: number;
  heroVideoOpacity: number;
  heroWispsEnabled: boolean;
  heroTextShadow: number;
  /** Dark scrim rendered directly behind the hero text block. */
  heroTextBackdropEnabled: boolean;
  heroTextBackdropColor: string;   // hex e.g. "#000000"
  heroTextBackdropOpacity: number; // 0-100
  heroTextBackdropBlur: number;    // 0-30 px
  heroTextBackdropRadius: number;  // 0-48 px
  /** Agency hero feature ticker scroll speed in seconds per loop (lower = faster). */
  heroTickerSpeed: number;         // 4-60 s
  italicText: boolean;
  tattooItalic: boolean;
  tileSize: number;
  tileOffsetX: number;
  tileOffsetY: number;
  magikGlowColor1: string;
  magikGlowColor2: string;
  magikGlowIntensity: number;
  magikGlowSpeed: number;
  textBgOpacity: number;
  artistCardScale: number;
  artistCardRadius: number;
  artistCardBorderWidth: number;
  artistCardBorderColor: string;
  artistCardFramePadding: number;
  artistCardFrameColor: string;
  artistCardVignetteOpacity: number;
  artistCardVignetteCenter: number;
  artistCardPositions: Record<string, { x: number; y: number }>;
  artistProfileZoom: Record<string, number>;
  artistHeroOrder: Record<string, string[]>;
  aboutHeroImages: string[];
  aboutStudioImages: { src: string; alt: string }[];
  phone?: string | null;
  contactEmail?: string | null;
}

const DEFAULT_CONFIG: SiteConfig = {
  heroOpacity: 35,
  heroCardBgOpacity: 97,
  heroVideoOpacity: 18,
  heroWispsEnabled: true,
  heroTextShadow: 20,
  heroTextBackdropEnabled: true,
  heroTextBackdropColor: "#000000",
  heroTextBackdropOpacity: 45,
  heroTextBackdropBlur: 10,
  heroTextBackdropRadius: 24,
  heroTickerSpeed: 28,
  italicText: true,
  tattooItalic: false,
  tileSize: 189,
  tileOffsetX: 213,
  tileOffsetY: 0,
  magikGlowColor1: "#8b5cf6",
  magikGlowColor2: "#ec4899",
  magikGlowIntensity: 20,
  magikGlowSpeed: 4,
  textBgOpacity: 70,
  artistCardScale: 50,
  artistCardRadius: 50,
  artistCardBorderWidth: 4,
  artistCardBorderColor: "#ffffff",
  artistCardFramePadding: 0,
  artistCardFrameColor: "#00ffff",
  artistCardVignetteOpacity: 0,
  artistCardVignetteCenter: 50,
  artistCardPositions: {
    "mikayla-alderton": { x: 39, y: 3 },
    "constance-masters": { x: 43, y: 14 },
    "kathleen-baker": { x: 59, y: 71 },
  },
  artistProfileZoom: {},
  artistHeroOrder: {},
  aboutHeroImages: [],
  aboutStudioImages: [],
  phone: null,
  contactEmail: null,
};

interface SiteConfigContextValue {
  config: SiteConfig;
  configLoaded: boolean;
  setConfig: (config: SiteConfig) => void;
  updateConfig: (partial: Partial<SiteConfig>) => void;
  saveAsDefault: () => Promise<void>;
  copyToClipboard: () => void;
  isSaving: boolean;
  saved: boolean;
  copied: boolean;
}

const CACHE_KEY = "mm-site-config";

function getCachedConfig(): SiteConfig {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return { ...DEFAULT_CONFIG, ...JSON.parse(cached) };
  } catch (_swallowedErr) { captureException(_swallowedErr); }
  return DEFAULT_CONFIG;
}

const SiteConfigContext = createContext<SiteConfigContextValue | null>(null);

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const { data: serverConfig } = useQuery<SiteConfig>({
    queryKey: ["/api/site-config"],
    queryFn: async () => {
      try {
        const data = await apiFetch(siteConfigContract);
        return { ...DEFAULT_CONFIG, ...(data as Partial<SiteConfig>) };
      } catch (err) {
        captureException(err);
        return DEFAULT_CONFIG;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [config, setConfig] = useState<SiteConfig>(getCachedConfig);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (serverConfig) {
      const merged = { ...DEFAULT_CONFIG, ...serverConfig };
      setConfig(merged);
      setConfigLoaded(true);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(serverConfig)); } catch { /* intentionally silent */ }
    }
  }, [serverConfig]);

  const updateConfig = useCallback((partial: Partial<SiteConfig>) => {
    setConfig((prev) => ({ ...prev, ...partial }));
    setSaved(false);
  }, []);

  const saveAsDefault = useCallback(async () => {
    setIsSaving(true);
    try {
      const r = await fetch("/api/site-config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      });
      if (!r.ok) {
        const msg = await r.text();
        captureException(new Error(`Failed to save config: ${r.status} ${msg}`));
        console.error("Failed to save config:", r.status, msg);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } finally {
      setIsSaving(false);
    }
  }, [config]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [config]);

  return (
    <SiteConfigContext.Provider
      value={{ config, configLoaded, setConfig, updateConfig, saveAsDefault, copyToClipboard, isSaving, saved, copied }}
    >
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error("useSiteConfig must be used within SiteConfigProvider");
  return ctx;
}
